/**
 * Customers Page
 *
 * Customer/client management with list, create, edit, and delete operations.
 * Uses accounting API to fetch data from database.
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
  getCustomerDisplayName,
  getCustomerTaxId,
} from '@business-assistant/shared/data/accounting';
import { useSearchParams, useFilteredCustomers } from '@business-assistant/shared/hooks';
import { useHubStore, useProjectsStore } from '@business-assistant/shared';
import type { Customer } from '@business-assistant/shared/data/types';
import * as customersApi from '../../api/customers';
import { CustomerDetailView } from './CustomerDetailView';
import '../accounting.css';

// =============================================================================
// TYPES
// =============================================================================

interface CustomersPageProps {
  onNavigate?: (path: string) => void;
  onBack?: () => void;
}

type ViewMode = 'list' | 'create' | 'edit' | 'detail';

/** Data structure for creating/updating customers */
interface CustomerCreateData {
  customerType: 'company' | 'individual';
  companyName?: string;
  firstName?: string;
  lastName?: string;
  vatNumber?: string;
  fiscalCode?: string;
  email: string;
  phone?: string;
  pec?: string;
  address: {
    street: string;
    city: string;
    postalCode: string;
    province: string;
    country: string;
  };
  sdiRecipientCode?: string;
  paymentTerms: string;
  notes?: string;
  isActive: boolean;
  tags: string[];
}

// =============================================================================
// COMPONENTS
// =============================================================================

function CustomerListItem({
  customer,
  onClick,
  onEdit,
  onDelete,
}: {
  customer: Customer;
  onClick: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onTagClick?: (tagId: string) => void;
}) {
  const displayName = getCustomerDisplayName(customer);
  const taxId = getCustomerTaxId(customer);

  return (
    <div
      onClick={onClick}
      className="accounting-card--row"
    >
      {/* Avatar */}
      <div className="accounting-icon--md accounting-flex--center">
        <svg
          width="24"
          height="24"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          {customer.customerType === 'company' ? (
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
            />
          ) : (
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
            />
          )}
        </svg>
      </div>

      {/* Info */}
      <div className="accounting-content">
        <div>{displayName}</div>
        <div>
          {customer.email}
          {taxId && ` | ${taxId}`}
        </div>
      </div>

      {/* Status Badge */}
      <span>
        {customer.isActive ? 'Attivo' : 'Inattivo'}
      </span>

      {/* Actions */}
      <div className="accounting-actions" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onEdit}
          title="Modifica"
        >
          <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
            />
          </svg>
        </button>
        <button
          onClick={onDelete}
          title="Elimina"
        >
          <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}

function CustomerForm({
  customer,
  onSave,
  onCancel,
}: {
  customer?: Customer;
  onSave: (data: CustomerCreateData) => void;
  onCancel: () => void;
}) {
  const [formData, setFormData] = useState<CustomerCreateData>({
    customerType: customer?.customerType || 'company',
    companyName: customer?.companyName || '',
    firstName: customer?.firstName || '',
    lastName: customer?.lastName || '',
    vatNumber: customer?.vatNumber || '',
    fiscalCode: customer?.fiscalCode || '',
    email: customer?.email || '',
    phone: customer?.phone || '',
    address: customer?.address || {
      street: '',
      city: '',
      postalCode: '',
      province: '',
      country: 'Italia',
    },
    sdiRecipientCode: customer?.sdiRecipientCode || '',
    pec: customer?.pec || '',
    paymentTerms: customer?.paymentTerms || 'Net 30',
    notes: customer?.notes || '',
    isActive: customer?.isActive ?? true,
    tags: customer?.tags || [],
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="accounting-form accounting-page--form">
      {/* Type Selection */}
      <div className="accounting-form__field">
        <label>Tipo cliente</label>
        <div className="accounting-form__radio-group">
          <label className="accounting-form__radio-option">
            <input
              type="radio"
              name="customerType"
              value="company"
              checked={formData.customerType === 'company'}
              onChange={() => setFormData({ ...formData, customerType: 'company' })}
            />
            <span>Azienda</span>
          </label>
          <label className="accounting-form__radio-option">
            <input
              type="radio"
              name="customerType"
              value="individual"
              checked={formData.customerType === 'individual'}
              onChange={() => setFormData({ ...formData, customerType: 'individual' })}
            />
            <span>Privato</span>
          </label>
        </div>
      </div>

      {/* Company/Individual Fields */}
      {formData.customerType === 'company' ? (
        <div className="accounting-form__field">
          <label>Ragione sociale *</label>
          <input
            type="text"
            value={formData.companyName}
            onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
            required
          />
        </div>
      ) : (
        <div className="accounting-grid--form-2col">
          <div className="accounting-form__field">
            <label>Nome *</label>
            <input
              type="text"
              value={formData.firstName}
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              required
            />
          </div>
          <div className="accounting-form__field">
            <label>Cognome *</label>
            <input
              type="text"
              value={formData.lastName}
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              required
            />
          </div>
        </div>
      )}

      {/* Tax ID Fields */}
      <div className="accounting-grid--form-2col">
        <div className="accounting-form__field">
          <label>Partita IVA</label>
          <input
            type="text"
            value={formData.vatNumber}
            onChange={(e) => setFormData({ ...formData, vatNumber: e.target.value })}
            placeholder="IT12345678901"
          />
        </div>
        <div className="accounting-form__field">
          <label>Codice Fiscale</label>
          <input
            type="text"
            value={formData.fiscalCode}
            onChange={(e) => setFormData({ ...formData, fiscalCode: e.target.value })}
          />
        </div>
      </div>

      {/* Contact Fields */}
      <div className="accounting-grid--form-2col">
        <div className="accounting-form__field">
          <label>Email *</label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            required
          />
        </div>
        <div className="accounting-form__field">
          <label>Telefono</label>
          <input
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          />
        </div>
      </div>

      {/* Address Fields */}
      <div className="accounting-form__field">
        <label>Indirizzo</label>
        <input
          type="text"
          value={formData.address.street}
          onChange={(e) =>
            setFormData({ ...formData, address: { ...formData.address, street: e.target.value } })
          }
          placeholder="Via/Piazza"
        />
        <div className="accounting-grid--form-3col">
          <input
            type="text"
            value={formData.address.city}
            onChange={(e) =>
              setFormData({ ...formData, address: { ...formData.address, city: e.target.value } })
            }
            placeholder="Citta"
          />
          <input
            type="text"
            value={formData.address.province}
            onChange={(e) =>
              setFormData({ ...formData, address: { ...formData.address, province: e.target.value } })
            }
            placeholder="Prov."
          />
          <input
            type="text"
            value={formData.address.postalCode}
            onChange={(e) =>
              setFormData({ ...formData, address: { ...formData.address, postalCode: e.target.value } })
            }
            placeholder="CAP"
          />
        </div>
      </div>

      {/* E-Invoice Fields */}
      <div className="accounting-grid--form-2col">
        <div className="accounting-form__field">
          <label>Codice SDI</label>
          <input
            type="text"
            value={formData.sdiRecipientCode}
            onChange={(e) => setFormData({ ...formData, sdiRecipientCode: e.target.value })}
            placeholder="7 caratteri"
            maxLength={7}
          />
        </div>
        <div className="accounting-form__field">
          <label>PEC</label>
          <input
            type="email"
            value={formData.pec}
            onChange={(e) => setFormData({ ...formData, pec: e.target.value })}
            placeholder="pec@example.it"
          />
        </div>
      </div>

      {/* Payment Terms */}
      <div className="accounting-form__field">
        <label>Termini di pagamento</label>
        <select
          value={formData.paymentTerms}
          onChange={(e) => setFormData({ ...formData, paymentTerms: e.target.value })}
        >
          <option value="immediate">Immediato</option>
          <option value="Net 7">Net 7 giorni</option>
          <option value="Net 15">Net 15 giorni</option>
          <option value="Net 30">Net 30 giorni</option>
          <option value="Net 60">Net 60 giorni</option>
          <option value="Net 90">Net 90 giorni</option>
        </select>
      </div>

      {/* Notes */}
      <div className="accounting-form__field">
        <label>Note</label>
        <textarea
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
        />
      </div>

      {/* Actions */}
      <div className="accounting-form__row">
        <button type="button" onClick={onCancel}>
          Annulla
        </button>
        <button type="submit">
          {customer ? 'Salva modifiche' : 'Crea cliente'}
        </button>
      </div>
    </form>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function CustomersPage({ onNavigate, onBack }: CustomersPageProps) {
  // URL parameter management
  const { params, filters, setParam, clearParam, hasActiveFilters } = useSearchParams();

  // Get data from stores for cross-module relations
  const { contacts } = useHubStore();
  const { projects } = useProjectsStore();

  // Local state
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);

  // Load customers from API on mount
  useEffect(() => {
    const loadCustomers = async () => {
      setLoading(true);
      const result = await customersApi.listCustomers();
      if (result.success && result.data) {
        // Map API customer type to component Customer type
        const mappedCustomers: Customer[] = result.data.map((c) => ({
          id: c.id,
          type: c.type,
          companyName: c.companyName,
          firstName: c.firstName,
          lastName: c.lastName,
          email: c.email,
          phone: c.phone,
          vatNumber: c.vatNumber,
          fiscalCode: c.fiscalCode,
          address: c.address,
          sdiRecipientCode: c.sdiRecipientCode,
          pec: c.pec,
          paymentTerms: c.paymentTerms,
          creditLimit: c.creditLimit,
          notes: c.notes,
          isActive: c.isActive,
          createdAt: c.createdAt,
          updatedAt: c.updatedAt,
        }));
        setCustomers(mappedCustomers);
      }
      setLoading(false);
    };
    loadCustomers();
  }, []);

  // Use filtered customers hook
  const { data: filteredCustomers, filteredCount, totalCount, isFiltered } = useFilteredCustomers(
    customers.filter((c) => {
      // Additional active filter from URL status param
      if (params.status === 'active') return c.isActive === true;
      if (params.status === 'inactive') return c.isActive === false;
      return true;
    }),
    filters
  );

  // Handle search input
  const handleSearch = useCallback((query: string) => {
    if (query.trim()) {
      setParam('s', query);
    } else {
      clearParam('s');
    }
  }, [setParam, clearParam]);

  // Handle status filter
  const handleStatusFilter = useCallback((status: string) => {
    if (status === 'all') {
      clearParam('status');
    } else {
      setParam('status', status);
    }
  }, [setParam, clearParam]);

  // Handle customer click - update URL with customer ID
  const handleCustomerClick = useCallback((customer: Customer) => {
    setParam('customer', customer.id);
    setSelectedCustomer(customer);
    setViewMode('detail');
  }, [setParam]);

  // Handle tag click - filter by tag
  const handleTagClick = useCallback((tagId: string) => {
    setParam('tag', tagId);
  }, [setParam]);

  // Handle create customer
  const handleCreateCustomer = (data: CustomerCreateData) => {
    const newCustomer: Customer = {
      ...data,
      id: `cst-${Date.now()}`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    setCustomers([newCustomer, ...customers]);
    setViewMode('list');
  };

  // Handle update customer
  const handleUpdateCustomer = (data: CustomerCreateData) => {
    if (!selectedCustomer) return;
    setCustomers(
      customers.map((c) =>
        c.id === selectedCustomer.id ? { ...c, ...data, updatedAt: new Date().toISOString() } : c
      )
    );
    setViewMode('list');
    setSelectedCustomer(null);
    clearParam('customer');
  };

  // Handle delete customer
  const handleDeleteCustomer = (customer: Customer) => {
    if (confirm(`Eliminare il cliente "${getCustomerDisplayName(customer)}"?`)) {
      setCustomers(customers.filter((c) => c.id !== customer.id));
    }
  };

  // Clear filters
  const handleClearFilters = useCallback(() => {
    clearParam('s');
    clearParam('tag');
    clearParam('status');
  }, [clearParam]);

  return (
    <div className="accounting-page accounting-page--narrow">
      {viewMode === 'list' && (
        <>
          {/* Toolbar */}
          <div className="accounting-toolbar">
            <div className="accounting-toolbar__search">
              <input
                type="text"
                placeholder="Cerca cliente..."
                defaultValue={params.s || ''}
                onChange={(e) => handleSearch(e.target.value)}
              />
            </div>
            <select
              value={params.status || 'all'}
              onChange={(e) => handleStatusFilter(e.target.value)}
            >
              <option value="all">Tutti</option>
              <option value="active">Attivi</option>
              <option value="inactive">Inattivi</option>
            </select>
            {hasActiveFilters && (
              <button
                onClick={handleClearFilters}
                className="accounting-flex--row accounting-flex--gap-sm"
              >
                <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Rimuovi filtri
              </button>
            )}
            <button
              onClick={() => setViewMode('create')}
              className="accounting-flex--row accounting-flex--gap-sm"
            >
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Nuovo Cliente
            </button>
          </div>

          {/* Customer List */}
          {filteredCustomers.length === 0 ? (
            <div className="accounting-empty">
              <svg
                width="48"
                height="48"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
              <p>Nessun cliente trovato</p>
            </div>
          ) : (
            <div className="accounting-list">
              {/* Filter results count */}
              {isFiltered && (
                <div>
                  Mostrati {filteredCount} di {totalCount} clienti
                </div>
              )}
              {filteredCustomers.map((customer) => (
                <CustomerListItem
                  key={customer.id}
                  customer={customer}
                  onClick={() => handleCustomerClick(customer)}
                  onEdit={() => {
                    setSelectedCustomer(customer);
                    setViewMode('edit');
                  }}
                  onDelete={() => handleDeleteCustomer(customer)}
                  onTagClick={handleTagClick}
                />
              ))}
            </div>
          )}
        </>
      )}

      {viewMode === 'create' && (
        <CustomerForm onSave={handleCreateCustomer} onCancel={() => setViewMode('list')} />
      )}

      {viewMode === 'edit' && selectedCustomer && (
        <CustomerForm
          customer={selectedCustomer}
          onSave={handleUpdateCustomer}
          onCancel={() => {
            setViewMode('list');
            setSelectedCustomer(null);
          }}
        />
      )}

      {viewMode === 'detail' && selectedCustomer && (
        <CustomerDetailView
          customer={selectedCustomer}
          contacts={contacts}
          projects={projects}
          onBack={() => {
            setViewMode('list');
            setSelectedCustomer(null);
            clearParam('customer');
          }}
          onEdit={() => setViewMode('edit')}
          onContactClick={(contactId) => {
            // Navigate to contact detail in Hub
            onNavigate?.(`hub/contacts/${contactId}`);
          }}
          onProjectClick={(projectId) => {
            // Navigate to project detail
            onNavigate?.(`progetti/${projectId}`);
          }}
          onInvoiceClick={(invoiceId) => {
            // Navigate to invoice detail
            onNavigate?.(`invoices?invoice=${invoiceId}`);
          }}
        />
      )}
    </div>
  );
}

export type { CustomersPageProps };
